import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Factory, Cog, Flame, Car, Package, Building2 } from "lucide-react";
import { Reveal, SectionTag, KineticLine } from "@/components/Reveal";
import { industries, process } from "@/data/site";

const icons = [Flame, Factory, Cog, Car, Package, Building2];

export default function Industries() {
  return (
    <div data-testid="industries-page">
      {/* PAGE HERO */}
      <section className="relative pt-40 pb-24 bg-ink noise overflow-hidden" data-testid="industries-hero">
        <div className="absolute inset-0">
          <img src="/assets/dubai-cargo.jpg" alt="Global shipping" className="w-full h-full object-cover opacity-25 grayscale-[40%]" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/40" />
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="font-tech text-xs uppercase tracking-[0.3em] text-copper mb-6"
          >
            Industries & Process
          </motion.p>
          <h1 className="font-display font-black uppercase text-white tracking-tighter leading-[0.95] text-5xl sm:text-6xl lg:text-7xl" data-testid="industries-headline">
            <KineticLine text="Feeding the" delay={0.15} />
            <KineticLine text="world's furnaces." delay={0.3} className="text-copper" />
          </h1>
        </div>
      </section>

      {/* INDUSTRIES GRID */}
      <section className="py-24 md:py-32" data-testid="industries-grid-section">
        <div className="max-w-7xl mx-auto px-6">
          <SectionTag no="01" label="Who We Supply" />
          <Reveal>
            <h2 className="font-display font-black text-navy tracking-tighter text-4xl md:text-5xl leading-[1.02] mb-16 max-w-2xl">
              Six sectors. One standard.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-gray-200" data-testid="industries-grid">
            {industries.map((ind, i) => {
              const Icon = icons[i];
              return (
                <Reveal key={ind.title} delay={(i % 3) * 0.08}>
                  <div
                    className="group h-full border-b border-r border-gray-200 p-10 bg-white hover:bg-navy transition-colors duration-500"
                    data-testid={`industry-card-${i}`}
                  >
                    <Icon size={30} className="text-copper" strokeWidth={1.5} />
                    <h3 className="font-display font-bold text-xl text-navy tracking-tight mt-6 group-hover:text-white transition-colors duration-500">
                      {ind.title}
                    </h3>
                    <p className="mt-3 text-sm text-gray-600 leading-relaxed group-hover:text-gray-300 transition-colors duration-500">
                      {ind.text}
                    </p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-ink noise py-24 md:py-32" data-testid="industries-process-section">
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <SectionTag no="02" label="How We Trade" light />
          <Reveal>
            <h2 className="font-display font-black text-white tracking-tighter text-4xl md:text-5xl leading-[1.02] mb-16">
              Enquiry to discharge, <span className="text-copper">in four moves.</span>
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {process.map((step, i) => (
              <Reveal key={step.no} delay={i * 0.1}>
                <div className="relative border border-white/10 p-8 h-full hover:border-copper/60 transition-colors duration-500" data-testid={`process-step-${i}`}>
                  <p className="font-display font-black text-6xl text-stroke-light">{step.no}</p>
                  <h3 className="font-display font-bold text-lg text-white tracking-tight mt-6">{step.title}</h3>
                  <p className="mt-3 text-sm text-gray-400 leading-relaxed">{step.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <Link
              to="/contact"
              data-testid="industries-cta"
              className="group mt-14 inline-flex items-center gap-3 bg-copper text-white font-tech text-xs uppercase tracking-[0.2em] px-7 py-4 hover:bg-white hover:text-navy transition-colors duration-300"
            >
              Start an Enquiry
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
